import React, { useState, useEffect } from 'react';
import { FunnelData } from '../types';
import { PRICING } from '../constants';
import { leadsService } from '../services/api';

const SalesFunnel: React.FC = () => {
  const [funnelData, setFunnelData] = useState<FunnelData[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFunnelData = async () => {
      try {
        setLoading(true);
        const response = await leadsService.getFunnelData({ 
          period: selectedPeriod 
        });
        
        // Processar os dados para incluir valores monetários baseados no preço fixo
        const processedData = response.map((stage: any, index: number, array: any[]) => {
          const totalLeads = array[0]?.count || 1;
          
          return {
            ...stage,
            percentage: (stage.count / totalLeads) * 100,
            valor_total: stage.status === 'fechado_ganho' 
              ? stage.count * PRICING.PRODUTO_PRECO  // Receita real confirmada
              : stage.status === 'fechado_perdido' 
              ? 0  // Nenhuma receita das perdidas
              : stage.count * PRICING.PRODUTO_PRECO, // Valor potencial
            taxa_conversao_proximo_stage: index < array.length - 1 
              ? ((array[index + 1]?.count || 0) / stage.count) * 100 
              : 0
          };
        });
        
        setFunnelData(processedData);
      } catch (error) {
        console.error('Erro ao carregar dados do funil:', error);
        console.log('Detalhes do erro:', error);
        
        // Tentar carregar todos os dados sem filtro de período
        try {
          const fallbackResponse = await leadsService.getFunnelData({});
          const fallbackProcessed = fallbackResponse.map((stage: any, index: number, array: any[]) => {
            const totalLeads = array[0]?.count || 1;
            
            return {
              ...stage,
              percentage: (stage.count / totalLeads) * 100,
              valor_total: stage.status === 'fechado_ganho' 
                ? stage.count * PRICING.PRODUTO_PRECO
                : stage.status === 'fechado_perdido' 
                ? 0
                : stage.count * PRICING.PRODUTO_PRECO,
              taxa_conversao_proximo_stage: index < array.length - 1 
                ? ((array[index + 1]?.count || 0) / stage.count) * 100 
                : 0
            };
          });
          setFunnelData(fallbackProcessed);
        } catch (fallbackError) {
          console.error('Erro no fallback também:', fallbackError);
          // Só usar dados mock se realmente não conseguir conectar
          setFunnelData([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadFunnelData();
  }, [selectedPeriod]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStageColor = (status: string) => {
    const colors: { [key: string]: string } = {
      novo: 'bg-blue-500',
      contato_inicial: 'bg-yellow-500',
      em_negociacao: 'bg-orange-500',
      proposta_enviada: 'bg-purple-500',
      fechado_ganho: 'bg-green-500',
      fechado_perdido: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStageName = (status: string) => {
    const names: { [key: string]: string } = {
      novo: '🆕 Novos',
      contato_inicial: '📞 Contato Inicial',
      em_negociacao: '💬 Em Negociação',
      proposta_enviada: '📋 Proposta Enviada',
      fechado_ganho: '✅ Fechado Ganho',
      fechado_perdido: '❌ Fechado Perdido'
    };
    return names[status] || status;
  };

  const getStageIcon = (status: string) => {
    const icons: { [key: string]: string } = {
      novo: '🆕',
      contato_inicial: '📞',
      em_negociacao: '💬',
      proposta_enviada: '📋',
      fechado_ganho: '✅',
      fechado_perdido: '❌'
    };
    return icons[status] || '📊';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <span className="ml-3 text-gray-600">Carregando funil dos dados reais...</span>
      </div>
    );
  }

  const totalLeads = funnelData[0]?.count || 0;
  const totalVendas = funnelData.find(f => f.status === 'fechado_ganho')?.valor_total || 0;
  const taxaConversaoGeral = totalLeads > 0 ? ((funnelData.find(f => f.status === 'fechado_ganho')?.count || 0) / totalLeads) * 100 : 0;
  const tempoMedioTotal = funnelData.reduce((acc, stage) => acc + Number(stage.tempo_medio_permanencia || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">🎯 Funil de Vendas</h2>
        <div className="flex space-x-2">
          {[
            { key: '7d', label: '7 dias' },
            { key: '30d', label: '30 dias' },
            { key: '90d', label: '90 dias' }
          ].map(period => (
            <button
              key={period.key}
              onClick={() => setSelectedPeriod(period.key as any)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === period.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Métricas Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm">🎯</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Leads no Funil
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {totalLeads}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm">✅</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Taxa de Conversão
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {taxaConversaoGeral.toFixed(1)}%
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-sm">💰</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Vendas Realizadas
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {formatCurrency(totalVendas)}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-orange-600 text-sm">⏱️</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Ciclo Médio Total
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {tempoMedioTotal.toFixed(1)} dias
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Funil Visual */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Visualização do Funil</h3>
        
        <div className="space-y-4">
          {funnelData.filter(stage => stage.status !== 'fechado_perdido').map((stage, index) => {
            const isLast = index === funnelData.filter(s => s.status !== 'fechado_perdido').length - 1;
            const nextStage = !isLast ? funnelData.filter(s => s.status !== 'fechado_perdido')[index + 1] : null;
            
            return (
              <div key={stage.status} className="relative">
                {/* Estágio Atual */}
                <div className="flex items-center">
                  {/* Barra do Funil */}
                  <div className="flex-1">
                    <div className="bg-gray-200 rounded-lg h-16 relative overflow-hidden">
                      <div
                        className={`${getStageColor(stage.status)} h-full rounded-lg transition-all duration-500 flex items-center justify-between px-4`}
                        style={{ width: `${Math.max(stage.percentage, 15)}%` }}
                      >
                        <div className="flex items-center text-white">
                          <span className="text-lg mr-2">{getStageIcon(stage.status)}</span>
                          <div>
                            <div className="font-medium">{getStageName(stage.status)}</div>
                            <div className="text-sm opacity-90">{stage.count} leads</div>
                          </div>
                        </div>
                        <div className="text-white text-right">
                          <div className="font-bold">{stage.percentage.toFixed(1)}%</div>
                          <div className="text-sm opacity-90">{formatCurrency(stage.valor_total)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Métricas Laterais */}
                  <div className="ml-4 text-right min-w-32">
                    <div className="text-sm text-gray-600">
                      Tempo médio: {Number(stage.tempo_medio_permanencia || 0).toFixed(1)}d
                    </div>
                    {!isLast && (
                      <div className="text-sm text-blue-600">
                        Conversão: {stage.taxa_conversao_proximo_stage.toFixed(1)}%
                      </div>
                    )}
                  </div>
                </div>

                {/* Seta de Conversão */}
                {!isLast && (
                  <div className="flex items-center justify-center py-2">
                    <div className="flex items-center text-gray-500">
                      <div className="text-xs mr-2">
                        -{stage.count - (nextStage?.count || 0)} leads
                      </div>
                      <div className="w-4 h-4 border-r-2 border-b-2 border-gray-400 transform rotate-45"></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabela Detalhada */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Detalhamento por Estágio</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estágio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % do Funil
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo Médio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taxa Conversão
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {funnelData.map((stage) => (
                <tr key={stage.status} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full ${getStageColor(stage.status)} mr-3`}></div>
                      <span className="text-sm font-medium text-gray-900">
                        {getStageName(stage.status)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {stage.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {stage.percentage.toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(stage.valor_total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {Number(stage.tempo_medio_permanencia || 0) > 0 ? `${Number(stage.tempo_medio_permanencia || 0).toFixed(1)} dias` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {stage.taxa_conversao_proximo_stage > 0 ? `${stage.taxa_conversao_proximo_stage.toFixed(1)}%` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">💡 Insights do Funil</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg">
            <div className="text-sm text-gray-600">Maior Gargalo</div>
            <div className="text-lg font-semibold text-red-600">
              {funnelData.reduce((min, stage) => 
                stage.taxa_conversao_proximo_stage > 0 && stage.taxa_conversao_proximo_stage < min.taxa_conversao_proximo_stage ? stage : min, 
                { taxa_conversao_proximo_stage: 100, status: 'N/A' }
              ).status !== 'N/A' ? getStageName(funnelData.reduce((min, stage) => 
                stage.taxa_conversao_proximo_stage > 0 && stage.taxa_conversao_proximo_stage < min.taxa_conversao_proximo_stage ? stage : min, 
                { taxa_conversao_proximo_stage: 100, status: 'N/A' }
              ).status) : 'Nenhum gargalo identificado'}
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg">
            <div className="text-sm text-gray-600">Melhor Conversão</div>
            <div className="text-lg font-semibold text-green-600">
              {funnelData.reduce((max, stage) => 
                stage.taxa_conversao_proximo_stage > max.taxa_conversao_proximo_stage ? stage : max, 
                { taxa_conversao_proximo_stage: 0, status: 'N/A' }
              ).status !== 'N/A' ? getStageName(funnelData.reduce((max, stage) => 
                stage.taxa_conversao_proximo_stage > max.taxa_conversao_proximo_stage ? stage : max, 
                { taxa_conversao_proximo_stage: 0, status: 'N/A' }
              ).status) : 'Nenhum identificado'}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg">
            <div className="text-sm text-gray-600">Oportunidade</div>
            <div className="text-lg font-semibold text-blue-600">
              {formatCurrency(funnelData.reduce((acc, stage) => acc + stage.valor_total, 0) - totalVendas)} em pipeline
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesFunnel;